// AccuracyChart.tsx - the "accuracy across the hand" line, ported 1:1 from the mockup's reportChart
// (local-mode-read-first-v2.html lines 1133-1144). A Preflop -> River polyline with a dot per street
// colored by that street's move class (var(--best|--inacc|--blun|...)), on a 50/75/100 gridline.
//
// Geometry and colors are transcribed exactly so the rendered SVG matches the locked DX6 surface; the
// only change is JSX instead of an HTML string. Fed by the real per-street grades via <ReportStreet[]>.
import type { ReportStreet } from './report';

// exact constants from the mockup reportChart.
const W = 300;
const H = 118;
const PAD_L = 24;
const PAD_R = 10;
const PAD_T = 10;
const PAD_B = 20;
const GRIDLINES = [50, 75, 100];

export function AccuracyChart({ streets }: { streets: ReportStreet[] }) {
  const xx = (i: number) => PAD_L + i * ((W - PAD_L - PAD_R) / (streets.length - 1));
  // accuracy 50..100 maps to the plot band (values below 50 clamp at the floor).
  const yy = (a: number) => PAD_T + (1 - (a - 50) / 50) * (H - PAD_T - PAD_B);

  const pts = streets.map((s, i) => [xx(i), yy(s.acc)] as const);
  const polyline = pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }} xmlns="http://www.w3.org/2000/svg">
      {/* gridlines + y labels */}
      {GRIDLINES.map((v) => {
        const y = yy(v);
        return (
          <g key={v}>
            <line
              x1={PAD_L}
              y1={y.toFixed(1)}
              x2={W - PAD_R}
              y2={y.toFixed(1)}
              stroke="#33413F"
              strokeWidth={1}
              strokeDasharray="2 3"
            />
            <text x={2} y={(y + 3).toFixed(1)} fill="#5E6C67" fontSize={8}>
              {v}
            </text>
          </g>
        );
      })}

      {/* the accuracy line */}
      <polyline points={polyline} fill="none" stroke="#177E5D" strokeWidth={2} />

      {/* a dot per street, colored by that street's move class */}
      {pts.map((p, i) => (
        <circle
          key={`dot-${i}`}
          cx={p[0].toFixed(1)}
          cy={p[1].toFixed(1)}
          r={4.5}
          fill={`var(--${streets[i].cls})`}
          stroke="#0b100e"
          strokeWidth={1.5}
        />
      ))}

      {/* street labels */}
      {streets.map((s, i) => (
        <text
          key={`lbl-${i}`}
          x={xx(i).toFixed(1)}
          y={H - 5}
          fill="#94A19C"
          fontSize={8.5}
          textAnchor="middle"
        >
          {s.name}
        </text>
      ))}
    </svg>
  );
}
